import type { IntlShape, MessageDescriptor } from 'react-intl';

import { FeatureFlagDeclarations } from '../../feature-flags/feature-flag-declarations';
import { BASE_SHIPMENTS_PATH, REFUND_ASSIST_SETTINGS_PATH, SHIPPED_RECENT_PATH } from '../../routes/constants';
import { linkIDs } from '../../services/link-service/links';
import type { SellerRecommendation } from '../../store/seller-recommendations/api/seller-recommendation';
import { ctaTypeNavigation, type CtaConfig } from './recommendation-cta-config';
import { RECOMMENDATION_DISPLAY_TYPES, type RecommendationDisplayConfig } from './recommendation-display-config';
import { sellerRecommendationMessages } from './messages';
import enableRefundAssistImage from './components/recommendation-popover-card/images/enable-refund-assist.svg';
import { DISMISSAL_STORAGE_KEYS, recommendationTypeEnableRefundAssist } from './seller-recommendation-types';

const REFUND_ASSIST_LOW_AMOUNT_THRESHOLD_USD = 22;

export type RecommendationRegistryEntry = {
  displayConfig: RecommendationDisplayConfig;
  cta?: CtaConfig;
  allowedRoutes?: string[];
  maxDismissalCount: number;
  featureFlag?: { key: string; defaultValue: boolean };
  dismissalStorageKey?: string;
  analytics?: {
    properties: (sellerId: string) => Record<string, unknown>;
  };
  /**
   * Optional per-type resolver for dynamic content values derived from recommendation metadata.
   * Use when the content MessageDescriptor has placeholders that need runtime values (e.g. formatted currency).
   * Return undefined if no interpolation is needed.
   */
  resolveRecommendationMetaDataValues?: (
    recommendation: SellerRecommendation | undefined,
    context: { intl: IntlShape; sellerCurrency: string },
  ) => Record<string, string | number> | undefined;
  /**
   * Optional resolver to override displayConfig.content based on recommendation metadata.
   * Return a MessageDescriptor to override, or undefined to use the default displayConfig.content.
   */
  resolveContent?: (recommendation: SellerRecommendation | undefined) => MessageDescriptor | undefined;
};

export const RECOMMENDATION_REGISTRY: Record<string, RecommendationRegistryEntry> = {
  [recommendationTypeEnableRefundAssist]: {
    allowedRoutes: [
      `${BASE_SHIPMENTS_PATH}${SHIPPED_RECENT_PATH}`,
      `${BASE_SHIPMENTS_PATH}/shipped-in-transit`,
      `${BASE_SHIPMENTS_PATH}/shipped-delivered`,
      `${BASE_SHIPMENTS_PATH}/shipped-delivery-exceptions`,
      `${BASE_SHIPMENTS_PATH}/shipped-voided`,
    ],
    maxDismissalCount: 2,
    featureFlag: FeatureFlagDeclarations.enableRefundAssistRecommendation,
    cta: {
      ctaType: ctaTypeNavigation,
      navigationUrl: REFUND_ASSIST_SETTINGS_PATH,
    },
    displayConfig: {
      displayType: RECOMMENDATION_DISPLAY_TYPES.POPOVER_CARD,
      title: sellerRecommendationMessages[recommendationTypeEnableRefundAssist].title,
      content: sellerRecommendationMessages[recommendationTypeEnableRefundAssist].description,
      buttonLabel: sellerRecommendationMessages[recommendationTypeEnableRefundAssist].buttonLabel,
      learnMoreLinkId: linkIDs.RefundAssist,
      isDismissible: true,
      illustrationSrc: enableRefundAssistImage,
    },
    dismissalStorageKey: DISMISSAL_STORAGE_KEYS.REFUND_ASSIST,
    resolveContent: (recommendation) => {
      const amount = recommendation?.metadata?.totalCarrierFeeEligible;
      if (amount != null && amount < REFUND_ASSIST_LOW_AMOUNT_THRESHOLD_USD) {
        return sellerRecommendationMessages[recommendationTypeEnableRefundAssist].descriptionLowAmount;
      }
      return undefined;
    },
    resolveRecommendationMetaDataValues: (recommendation, { intl, sellerCurrency }) => {
      const totalCarrierFeeEligible = recommendation?.metadata?.totalCarrierFeeEligible;
      if (totalCarrierFeeEligible == null || totalCarrierFeeEligible < REFUND_ASSIST_LOW_AMOUNT_THRESHOLD_USD) return undefined;
      return {
        amount: intl.formatNumber(totalCarrierFeeEligible, { style: 'currency', currency: sellerCurrency, maximumFractionDigits: 0 }),
      };
    },
    analytics: {
      properties: (sellerId) => ({
        recommendation_type: recommendationTypeEnableRefundAssist,
        seller_id: sellerId,
      }),
    },
  },
};
